import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Quote } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../i18n/translations";
import { SERVICES } from "../data/mock";
import RefLogo from "./RefLogo";

// Single client case-study card
const ReferenceCard = ({ r, dark = false }) => {
  const { lang } = useLanguage();
  const t = translations[lang].references;
  const service = SERVICES.find((s) => s.id === r.service);

  return (
    <article
      className={`group relative h-full rounded-2xl p-7 flex flex-col transition-all duration-300 hover:-translate-y-1 ${
        dark
          ? "bg-white/[0.04] border border-white/10 hover:border-amber-500/40"
          : "bg-white border border-slate-200 hover:shadow-xl"
      }`}
      data-testid={`reference-card-${r.slug}`}
    >
      <div className="flex items-start justify-between gap-4">
        <Link to={`/referanslar/${r.slug}`} className="flex items-center gap-3 group/name" data-testid={`reference-card-link-${r.slug}`}>
          <RefLogo name={r.name} logo={r.logo} dark={dark} />
          <div>
            <h3 className={`font-display font-bold text-lg leading-tight group-hover/name:text-amber-500 transition-colors ${dark ? "text-white" : "text-navy-900"}`}>{r.name}</h3>
            <p className={`text-xs mt-0.5 ${dark ? "text-slate-400" : "text-slate-500"}`}>{r.sector?.[lang]}</p>
          </div>
        </Link>
        {r.metric && (
          <div className="text-right shrink-0">
            <div className="font-display font-extrabold text-2xl text-amber-500 leading-none">{r.metric}</div>
            <div className={`text-[11px] mt-1 ${dark ? "text-slate-400" : "text-slate-500"}`}>{r.metric_label?.[lang]}</div>
          </div>
        )}
      </div>

      <div className="mt-5 relative">
        <Quote className={`absolute -top-1 -left-1 w-5 h-5 ${dark ? "text-amber-500/30" : "text-amber-500/25"}`} />
        <p className={`pl-6 text-sm leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>{r.summary?.[lang]}</p>
      </div>

      <div className={`mt-5 pt-5 border-t flex-1 flex flex-col justify-end ${dark ? "border-white/10" : "border-slate-100"}`}>
        {r.testimonial?.[lang] && (
          <div className={`mb-5 rounded-xl p-4 ${dark ? "bg-amber-500/10 border border-amber-500/15" : "bg-amber-50 border border-amber-100"}`} data-testid={`reference-testimonial-${r.slug}`}>
            <p className={`text-sm italic leading-relaxed ${dark ? "text-slate-200" : "text-navy-900"}`}>“{r.testimonial[lang]}”</p>
            {r.contact_name && (
              <p className={`mt-2 text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>
                <span className={`font-semibold ${dark ? "text-amber-400" : "text-amber-600"}`}>{r.contact_name}</span>
                {r.contact_title?.[lang] && <> · {r.contact_title[lang]}</>}
              </p>
            )}
          </div>
        )}
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-amber-500">{t.result}</p>
        <p className={`mt-1.5 font-medium ${dark ? "text-white" : "text-navy-900"}`}>{r.result?.[lang]}</p>
        <div className="mt-4 flex items-center justify-between gap-3">
          {service ? (
            <Link
              to={`/hizmetlerimiz/${service.slug}`}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold transition-colors ${
                dark ? "text-slate-400 hover:text-amber-400" : "text-slate-500 hover:text-amber-600"
              }`}
            >
              {service.tag[lang]} <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          ) : <span />}
          <Link
            to={`/referanslar/${r.slug}`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-500 hover:gap-2.5 transition-all"
            data-testid={`reference-card-read-${r.slug}`}
          >
            {t.readStory} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default ReferenceCard;
